
import React, { useContext, useState, useMemo, useCallback, ReactElement } from 'react';
import { Modal, ModalBody, Form, } from 'react-bootstrap';
import { UserContext } from 'contexts/user/UserContextProvider';
import IntlMessages from 'helpers/IntlMessages';
import { useMutation, useQuery } from '@apollo/client';
import MultilingualHtmlParser from 'components/Multilingual/MultilingualHtmlParser';
import Editor from 'components/TxtEditor/Editor';
import { Field, Formik, FormikValues } from 'formik';
import * as Yup from 'yup';
import { getTranslatedString } from 'helpers/utils-typescript';
import NextPreviousButton from 'components/common/NextPreviousButton';
import { updateMultilingualJson } from 'helpers/Utils';
import LifebuoyIcon from 'mdi-react/LifebuoyIcon';
import { ContextHelpModalProps, FormInitialValues, CREATE_CONTEXT_HELP_ENTRY, UPDATE_CONTEXT_HELP_ENTRY, CONTEXT_HELP_ENTRY } from 'components/common/ContextHelp';
import ModalHeader from 'react-bootstrap/ModalHeader';

interface SpecimenContextHelpProps extends ContextHelpModalProps {
  specimenData: {
    dataWtUnits: string,
    icon: ReactElement<{ height: string, className: string }>,
    titleTranslationId: string,
  },
}

 
/** Renders a modal that is shown when you tap on specimen stats (height, girth, age, ecosystem services) in mobile view.
 *  On the top, the ecosystem service icon is shown, together with value and units.
 *  Under that, the context help text is shown.
 */
const SpecimenContextHelpModal = ({
  showContextHelpModal,
  setShowContextHelpModal,
  currentLang,
  contextHelpId,
  specimenData,
}: SpecimenContextHelpProps): React.ReactElement => {
  const { currentUser } = useContext(UserContext);
  const [hasSubmit, setHasSubmit] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const [createContextHelpEntry] = useMutation(CREATE_CONTEXT_HELP_ENTRY);
  const [updateContextHelpEntry] = useMutation(UPDATE_CONTEXT_HELP_ENTRY);
  const [displaySpinner, setDisplaySpinner] = useState(false);

  const onCloseModal = () => {
    setShowContextHelpModal(false);
  };

  /* don't make a query unless the modal is open, and if we don't have an id */
  const { loading, error, data, refetch } =
    useQuery(CONTEXT_HELP_ENTRY, { variables: { id: contextHelpId }, skip: (!contextHelpId || !showContextHelpModal) });

  const content = useMemo(() => {
    if (loading || !data || error) {
      return null;
    }
    return data?.contextHelpEntry?.content;
  }, [loading, error, data]);

  const initialValues = useMemo(() => {
    const formInitialValues: FormInitialValues = { content: '' };
    if (!content) {
      return formInitialValues;
    }
    formInitialValues.content = getTranslatedString(content, currentLang);
    return formInitialValues;
  }, [content, currentLang]);

  const handleSubmit = useCallback((values: FormikValues) => { // this is duplicated from ContextHelp.tsx and could be refactored
    if (isDirty) {
      setDisplaySpinner(true);
      const { content: formContent } = values;
      const updatedFormContent = updateMultilingualJson(content, formContent, currentLang);
      if (!content) {
        createContextHelpEntry({
          variables: {
            id: contextHelpId,
            content: updatedFormContent,
          }
        }).then(() => {
          refetch();
          setDisplaySpinner(false);
          setShowContextHelpModal(false);
        }).catch((error) => {
          console.log(error);
          setDisplaySpinner(false);
        });
      } else {
        updateContextHelpEntry({
          variables: {
            id: contextHelpId,
            content: updatedFormContent,
          }
        }).then((result) => {
          const updatedContent = result?.data?.updateContextHelpEntry?.contextHelpEntry.content;
          if (updatedContent) {
            initialValues.content = getTranslatedString(updatedContent, currentLang);
            setDisplaySpinner(false);
            setShowContextHelpModal(false);
          }
        }).catch((error) => {
          console.log(error);
          setDisplaySpinner(false);
        });
      }
    } else {
      setShowContextHelpModal(false);
    }

  }, [
    content,
    createContextHelpEntry,
    currentLang,
    contextHelpId,
    isDirty,
    updateContextHelpEntry,
    setShowContextHelpModal,
    initialValues,
    refetch,
  ]);


  const validationSchema = Yup.object().shape({
    content: Yup.string().nullable(),
  });

  return (
    <Modal
      size='lg'
      show={showContextHelpModal}
      onHide={() => setShowContextHelpModal(!showContextHelpModal)}
      className="rounded-modal"
      scrollable
    >

      <ModalHeader closeButton className="ml-3 mr-3 mt-3 mb-0 p-0 d-flex flex-column">
        <div className="mx-auto py-3 order-3 d-flex flex-column">
          {specimenData?.icon}
          <h2 className='color-theme-1 text-center my-1'>
            <IntlMessages id={specimenData?.titleTranslationId} />
          </h2>
          <div className='text-center'>
            {specimenData?.dataWtUnits}
          </div>
        </div>
      </ModalHeader>

      <ModalBody className="px-3 pb-3 ">

        { /* "create/edit context help entry" heading for admin */}
        {(currentUser?.isAdmin) && (
          (!content)
            ? <h2><LifebuoyIcon className="orange-color" size="35" /> <IntlMessages id='context-help-modal.title-new' /></h2>
            : <h2><LifebuoyIcon className="orange-color" size="35" /> <IntlMessages id='context-help-modal.title-edit' /></h2>
        )}

        {(loading)
          ? /* show a spinner while loading */
          <>
            <div className="d-flex justify-content-center">
              <LifebuoyIcon size={90} className="orange-color mb-4" />
            </div>
            <div className="d-flex justify-content-center">
              <div className="loading-relative-position mt-2 mb-2" />
            </div>
          </>
          :
          <>

            { /* render html content for non-admin */}
            {content && !currentUser?.isAdmin &&
              <div className="html-parser card-full-width-img mb-4">
                <MultilingualHtmlParser multilingualHtml={content} />
              </div>
            }

            { /* html editor for admin */}
            {currentUser?.isAdmin &&
              <Formik
                initialValues={initialValues}
                validationSchema={validationSchema}
                validateOnMount={true}
                onSubmit={handleSubmit}
              >
                {({ values, handleSubmit, isValid, dirty }) => (
                  <Form className="av-tooltip tooltip-label-right" onSubmit={handleSubmit}>
                    <Field
                      name="content"
                      className='form-control-editor'
                      component={Editor}
                      value={values.content}
                    />

                    { /* close/save buttons for admin */}
                    <div className="d-flex row justify-content-center mt-3">
                      <NextPreviousButton
                        onClick={onCloseModal}
                        buttonTextId='general.close'
                        className="mr-2" />
                      <NextPreviousButton
                        buttonTextId='general.save'
                        className={`ml-2 btn-yellow ${displaySpinner ? 'disabled' : ''}`}
                        submit
                        disabled={!hasSubmit ? false : !isValid}
                        displaySpinner={displaySpinner}
                        onClick={() => {
                          setHasSubmit(true);
                          setIsDirty(dirty);
                        }} />
                    </div>
                  </Form>
                )}
              </Formik>
            }
          </>
        }
      </ModalBody>
      { /* close button for non-admin */}
      {!currentUser?.isAdmin &&
        <div className="text-center py-4">
          <NextPreviousButton
            onClick={onCloseModal}
            buttonTextId='general.close'
          />
        </div>
      }
    </Modal>
  );
};

export default SpecimenContextHelpModal;
